import React, { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';
import { Button } from './ui/button';
import { useServiceWorkerUpdates } from '../hooks/useServiceWorkerUpdates';

const EXCLUDED_PATHS = ['/', '/login', '/register'];

export const ServiceWorkerUpdateBanner: React.FC = () => {
  const { t } = useLocale();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showUpdatePrompt, confirmUpdate, dismissUpdate } = useServiceWorkerUpdates();

  const forceShowFromQuery = searchParams.get('_showUpdatePanel') === 'true';
  const showBanner = showUpdatePrompt || forceShowFromQuery;

  const stripShowUpdatePanelParam = useCallback(() => {
    if (!searchParams.has('_showUpdatePanel')) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete('_showUpdatePanel');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleConfirm = useCallback(() => {
    stripShowUpdatePanelParam();
    confirmUpdate();
  }, [stripShowUpdatePanelParam, confirmUpdate]);

  const handleDismiss = useCallback(() => {
    stripShowUpdatePanelParam();
    dismissUpdate();
  }, [stripShowUpdatePanelParam, dismissUpdate]);

  if (!showBanner || EXCLUDED_PATHS.includes(pathname)) {
    return null;
  }

  const banner = (
    <div
      role="alert"
      style={{
        position: 'fixed',
        left: '50%',
        top: '1rem',
        transform: 'translateX(-50%)',
        zIndex: 10000,
        maxWidth: 'calc(100vw - 1rem)',
        boxSizing: 'border-box',
        backgroundColor: 'var(--scribane-black)',
        color: '#ffffff',
        padding: '0.75rem 1.5rem',
        borderRadius: '8px',
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: '0.75rem',
        pointerEvents: 'auto',
      }}
    >
      <span style={{ fontSize: '0.875rem', textAlign: 'center' }}>
        {t('pwa_new_version_available')}
      </span>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
        }}
      >
        <Button size="sm" onClick={handleConfirm}>
          {t('pwa_update_now')}
        </Button>
        <Button size="sm" variant="outline" onClick={handleDismiss}>
          {t('pwa_update_later')}
        </Button>
      </div>
    </div>
  );

  return createPortal(banner, document.body);
};

